/** @jsxImportSource @emotion/react */

import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { useCallback, useMemo, useState } from 'react'
import { RootState } from '@app/redux/store'
import { useSelector } from 'react-redux'
import { HazardDetail } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { Alert, Box, Button, Popover, useTheme } from '@mui/material'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { css } from '@emotion/react'
import { useLayers } from '@app/contexts/LayerContext'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'

import {
  layerDataHazardIdTransformer,
  useLayerDataFetcher,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { HazardGroup } from '../../../..'
import { layersTabParent, mapLayerHeader } from './LayersTab.styles'
import { Accordion } from '@app/components/Atoms/Accordion'
import { HazardControls_Legacy } from '../HazardControls_Legacy'
import { SideDrawerDivider } from '@app/components/Molecules/RiskSideDrawerContent/components/SideDrawerDivider'
import { SideDrawerFooter } from '@app/components/Molecules/RiskSideDrawerContent/components/SideDrawerFooter'
import { EmptySideDrawer } from '@app/components/Molecules/RiskSideDrawerContent/tabs/EmptyTab'
import { LayersOrganizer_Legacy } from '../LayersOrganizer'
import { ActiveWorkspaceHeader } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/ActiveWorkspaceHeader/ActiveWorkspaceHeader'

export const LayersTab_Legacy = () => {
  const theme = useTheme()
  const { prepareAndUpdateLayers } = useLayers()
  const { setActiveLiveLayers, liveMapSelected } = useLiveContext()
  const { setShowAddLayersModalTab, setHazardOptionState } = useMap()

  let { layers } = useSelector((state: RootState) => state.riskMap)
  if (!liveMapSelected) {
    layers = layers.filter((layer) => layer.layerType !== 'live')
  }

  const { liveLayers, liveData } = useLiveContext()
  const atLeastOneLiveLayerUnavailable = useMemo(
    () =>
      liveLayers.some(
        (liveLayer) => liveLayer.isError && layers.some((layer) => layer.id == liveLayer.id),
      ),
    [liveLayers, layers],
  )
  const atLeastOneLiveLayerEmpty = useMemo(
    () =>
      liveLayers.some((liveLayer) => {
        // Don't show layer empty message if layer is erroring
        if (liveLayer.isError && layers.some((layer) => layer.id == liveLayer.id)) return false

        const data = liveData[liveLayer.id]
        return (
          data != null &&
          data.features.length === 0 &&
          layers.some((layer) => layer.id == liveLayer.id)
        )
      }),
    [liveLayers, liveData, layers],
  )

  const layersData = useLayerDataFetcher()
  const transformedLayerData = layerDataHazardIdTransformer(layersData)

  const clientName = localStorage.getItem('client_name')

  const [deleteAllLayersButtonAnchor, setDeleteAllLayersButtonAnchor] =
    useState<HTMLButtonElement | null>(null)

  const hazardLayers = layers.filter((layer) => layer.layerType === 'hazard')

  /**
   * Get the dialog layer data that matches the hazardMapLayers
   */
  const filteredLayersData = useMemo(() => {
    const hazardMapLayers = layers.filter((layer: MapLayer) => layer.layerType === 'hazard')
    const res = layersData?.hazardDialogData.hazardDetails.filter((hazardDetail: HazardDetail) => {
      // filter based on title
      return hazardMapLayers.some((layer: MapLayer) => {
        if (layer.hazard_id) {
          return hazardDetail.scenarios.some((scenario) => {
            return layer.hazard_id === scenario.hazard_id
          })
        }
        return layer.hazard_title === hazardDetail.title
      })
    })
    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersData, layers, clientName])

  function groupHazardsByType(hazardLayers: MapLayer[]): HazardGroup[] {
    const hazardLayersByType: HazardGroup[] = []
    hazardLayers.forEach((readonly_layer) => {
      const layer = { ...readonly_layer }
      const hazardType = layer.hazard_title ?? layer.display_name.split('(')[0].trim()
      let hazardScenario = layer.display_name
      if (layer.scenario_name) {
        hazardScenario = layer.scenario_name
      } else if (hazardScenario.includes('(')) {
        hazardScenario = hazardScenario.split('(')[1].split(')')[0].trim()
        layer.scenario_name = hazardScenario
      }
      const hazardGroup = hazardLayersByType.find((group) => group.hazard_type === hazardType)
      if (hazardGroup) {
        hazardGroup.scenarios.push({ scenario: hazardScenario, layer })
      } else {
        hazardLayersByType.push({
          hazard_type: hazardType,
          scenarios: [{ scenario: hazardScenario, layer }],
        })
      }
    })
    return hazardLayersByType
  }

  const handleConfirmClearAllLayers = () => {
    prepareAndUpdateLayers({ layers: [] })
    setDeleteAllLayersButtonAnchor(null)
    setHazardOptionState({})
    if (liveMapSelected) {
      setActiveLiveLayers([])
    }
  }

  const hasHazardControls = filteredLayersData?.some((hazardDetail) => hazardDetail.form.length > 0)
  const hasAtLeastOneUsableControl = useCallback(
    (hazardLayers: MapLayer[]) => {
      // if there is no transformed data (no hazard_id), do the same operations but with filteredLayersData instead
      if (!transformedLayerData) {
        return filteredLayersData?.some((hazardDetail) => {
          const paramObj: { [key: string]: (string | number)[] } = {}
          for (const scenario of hazardDetail.scenarios) {
            for (const paramKey of Object.keys(scenario.parameters)) {
              if (paramObj[paramKey]) {
                paramObj[paramKey].push(scenario.parameters[paramKey])
              } else {
                paramObj[paramKey] = [scenario.parameters[paramKey]]
              }
            }
          }
          return Object.keys(paramObj).some((paramKey) => {
            return paramObj[paramKey].length > 1
          })
        })
      } else {
        const hazardLayersGrouped = groupHazardsByType(hazardLayers)
        const filteredTransformedLayerData = Object.keys(transformedLayerData).filter((key) => {
          const foundHazardLayer = hazardLayersGrouped.find((hazardLayer) => {
            return hazardLayer.scenarios.some((scenario) => {
              return `${scenario.layer.hazard_id}` === `${key}`
            })
          })
          return !!foundHazardLayer
        })
        return filteredTransformedLayerData.some((hazardId) => {
          if (
            !hazardLayersGrouped.some((layer) =>
              layer.scenarios.some((scenario) => {
                return scenario.layer.hazard_id == hazardId
              }),
            )
          ) {
            return false
          }
          return Object.keys(transformedLayerData[hazardId].availableParameterValues).some(
            (parameterKey) => {
              return (
                transformedLayerData[hazardId].availableParameterValues[parameterKey].length > 1
              )
            },
          )
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hazardLayers, clientName, transformedLayerData, filteredLayersData],
  )

  return layers.length > 0 ? (
    <>
      <ActiveWorkspaceHeader />
      <Box css={layersTabParent} className="tutorial-layer-organizer">
        {hazardLayers.length > 0 &&
          hasAtLeastOneUsableControl(hazardLayers) &&
          hasHazardControls && (
            <>
              <Box
                css={css`
                  margin: 8px;
                `}
              >
                <Accordion
                  title="Scenario Controls"
                  variant="panel"
                  level="h3"
                  body={
                    <HazardControls_Legacy
                      layersData={layersData}
                      scenarioByHazardId={transformedLayerData}
                      hazardIds={layers
                        .filter((l) => l.layerType == 'hazard')
                        .map((l) => l.hazard_id ?? l.display_name)
                        .toSorted()}
                    />
                  }
                  defaultExpanded
                />
              </Box>
              <SideDrawerDivider />
            </>
          )}
        <Box className="tutorial-maplayers">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {atLeastOneLiveLayerUnavailable && (
              <Alert severity="error">
                One or more layers are unavailable. Affected layer/s highlighted below.
              </Alert>
            )}
            {atLeastOneLiveLayerEmpty && (
              <Alert severity="info">Highlighted layers have no active alerts.</Alert>
            )}
          </Box>
          <Box css={mapLayerHeader}>
            <Box sx={{ alignSelf: 'flex-end', display: 'flex', gap: '8px' }}>
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowAddLayersModalTab('elements')
                }}
                data-dd-action-name="add-layers-button-layers-tab"
                variant="text"
                size="small"
                css={css`
                  align-self: flex-end;
                  color: ${theme.palette.blue.main};
                `}
              >
                + Add layers
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleteAllLayersButtonAnchor(e.currentTarget)
                }}
                id="clear-all-layers-button"
                variant="text"
                size="small"
                css={css`
                  align-self: flex-end;
                  color: ${theme.palette.blue.main};
                `}
              >
                Clear all
              </Button>
              <Popover
                id={deleteAllLayersButtonAnchor ? 'clear-all-layers-button' : undefined}
                open={deleteAllLayersButtonAnchor?.id === 'clear-all-layers-button'}
                anchorEl={deleteAllLayersButtonAnchor}
                onClose={() => setDeleteAllLayersButtonAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleConfirmClearAllLayers()
                  }}
                >
                  Clear
                </Button>
              </Popover>
            </Box>
          </Box>
          <LayersOrganizer_Legacy />
        </Box>
      </Box>
      <SideDrawerFooter />
    </>
  ) : (
    <EmptySideDrawer
      text={
        liveMapSelected
          ? 'Add layers to start building your operating picture'
          : 'No layers are visible on the map. Add layers to gain insights'
      }
      type="element"
      buttonText="Add layers"
    />
  )
}
